import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLibrary } from '../lib/Library';
import { haptic, isIOS, isStandalone } from '../lib/touch';

/**
 * The phone's half of the shell: the tab bar, the Sports/Live TV switch that
 * sits under the header on those two pages, pull to refresh, and the one-time
 * Add to Home Screen hint. All of it is styled away above 760 px (mobile.css),
 * except the hint, which only ever renders for iOS Safari.
 */

const typeOf = (location) => new URLSearchParams(location.search).get('type');

const TABS = [
  {
    to: '/home',
    label: 'Home',
    active: (l) => l.pathname === '/home',
    icon: 'M12 3.2 2.8 10.6l1.2 1.6 1.5-1.2V20h5v-5.5h3V20h5v-9l1.5 1.2 1.2-1.6L12 3.2z'
  },
  {
    to: '/discover?type=movie',
    label: 'Films',
    active: (l) => l.pathname === '/discover' && typeOf(l) === 'movie',
    icon: 'M4 4h16v16H4V4zm2 2v2h2V6H6zm10 0v2h2V6h-2zM6 10v4h12v-4H6zm0 6v2h2v-2H6zm10 0v2h2v-2h-2zM10 6v2h4V6h-4zm0 10v2h4v-2h-4z'
  },
  {
    to: '/discover?type=series',
    label: 'Series',
    active: (l) => l.pathname === '/discover' && typeOf(l) === 'series',
    icon: 'M3 6h18v11H3V6zm2 2v7h14V8H5zm3 11h8v2H8v-2zM8.5 2.5 12 5l3.5-2.5 1 1.4L13.7 6h-3.4L7.5 3.9l1-1.4z'
  },
  {
    to: '/sports',
    label: 'Live',
    active: (l) => l.pathname === '/sports' || l.pathname === '/tv',
    icon: 'M12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6zM6.3 6.3l1.4 1.4a6 6 0 0 0 0 8.6l-1.4 1.4a8 8 0 0 1 0-11.4zm11.4 0a8 8 0 0 1 0 11.4l-1.4-1.4a6 6 0 0 0 0-8.6l1.4-1.4zM3.5 3.5l1.4 1.4a10 10 0 0 0 0 14.2l-1.4 1.4a12 12 0 0 1 0-17zm17 0a12 12 0 0 1 0 17l-1.4-1.4a10 10 0 0 0 0-14.2l1.4-1.4z'
  },
  {
    to: '/list',
    label: 'My List',
    active: (l) => l.pathname === '/list',
    icon: 'M6 3h12v18l-6-4.2L6 21V3zm2 2v12.2l4-2.8 4 2.8V5H8z'
  }
];

export const TabBar = () => {
  const location = useLocation();
  const { items } = useLibrary();
  // The tabs belong to the page under the title, not the title sheet itself
  const shown = location.state?.background || location;

  return (
    <nav className="tabbar" aria-label="Sections">
      {TABS.map((tab) => {
        const active = tab.active(shown);
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={`tab-item${active ? ' is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
            onClick={() => {
              haptic(6);
              // Tapping the tab you are on goes back to the top of it
              if (active) window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <svg viewBox="0 0 24 24" width="23" height="23" fill="currentColor" aria-hidden="true">
              <path d={tab.icon} />
            </svg>
            <span>{tab.label}</span>
            {tab.to === '/list' && items.length > 0 && <span className="tab-count">{items.length}</span>}
          </Link>
        );
      })}
    </nav>
  );
};

/** Sports and Live TV share the Live tab; this picks between them. */
export const LiveSwitch = () => {
  const { pathname } = useLocation();
  if (pathname !== '/sports' && pathname !== '/tv') return null;
  return (
    <div className="live-switch" role="tablist" aria-label="Live">
      <Link to="/sports" role="tab" aria-selected={pathname === '/sports'} className={pathname === '/sports' ? 'is-active' : ''}>
        Sports
      </Link>
      <Link to="/tv" role="tab" aria-selected={pathname === '/tv'} className={pathname === '/tv' ? 'is-active' : ''}>
        Live TV
      </Link>
    </div>
  );
};

// Pulled this far past the top, letting go refreshes
const PULL_ARM_PX = 72;

/**
 * Pull to refresh, for the home-screen app only.
 *
 * A browser tab already has its own (Safari and Chrome both), and a second one
 * on top would fight it. Opened from the home screen there is none, so this
 * stands in: a pull from the very top of the page, past the threshold, reloads
 * the app - which also picks up a new version straight away. Ignored while a
 * title sheet or any other overlay has the page locked.
 */
export const PullToRefresh = () => {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef(null);
  const armed = useRef(false);

  useEffect(() => {
    if (!isStandalone()) return undefined;

    const locked = () =>
      document.body.style.overflow === 'hidden' || document.querySelector('.modal-scrim, .sheet-scrim');

    const onStart = (event) => {
      if (window.scrollY > 0 || event.touches.length !== 1 || locked()) {
        start.current = null;
        return;
      }
      start.current = { x: event.touches[0].clientX, y: event.touches[0].clientY, live: false };
    };

    const onMove = (event) => {
      const s = start.current;
      if (!s) return;
      const dy = event.touches[0].clientY - s.y;
      const dx = event.touches[0].clientX - s.x;
      if (!s.live) {
        // A sideways flick along a rail, or a scroll up the page, is not a pull
        if (dy < 8 || Math.abs(dx) > dy) {
          if (Math.abs(dx) > 8 || dy < -8) start.current = null;
          return;
        }
        s.live = true;
      }
      event.preventDefault();
      // Resistance: the indicator moves less the further it goes
      const distance = Math.min(140, dy * 0.55);
      setPull(distance);
      const nowArmed = distance >= PULL_ARM_PX;
      if (nowArmed && !armed.current) haptic(10);
      armed.current = nowArmed;
    };

    const onEnd = () => {
      const s = start.current;
      start.current = null;
      if (!s?.live) return;
      if (armed.current) {
        setRefreshing(true);
        setPull(PULL_ARM_PX);
        window.setTimeout(() => window.location.reload(), 150);
      } else {
        setPull(0);
      }
      armed.current = false;
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  if (!pull && !refreshing) return null;
  const progress = Math.min(1, pull / PULL_ARM_PX);
  return (
    <div
      className={`ptr${progress >= 1 ? ' is-armed' : ''}`}
      style={{ transform: `translateY(${pull - 48}px)`, opacity: Math.min(1, progress * 1.4) }}
      aria-hidden="true"
    >
      {refreshing ? (
        <span className="player-ring" />
      ) : (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style={{ transform: `rotate(${progress * 270}deg)` }}>
          <path d="M12 4a8 8 0 1 0 7.7 10h-2.1A6 6 0 1 1 12 6c1.7 0 3.1.7 4.2 1.8L13 11h7V4l-2.3 2.3A8 8 0 0 0 12 4z" />
        </svg>
      )}
    </div>
  );
};

const HINT_KEY = 'installHint.shown';

/**
 * Add to Home Screen, said once, on an iPhone or iPad in Safari.
 *
 * iOS has no install prompt of its own and no API to raise one, so the only
 * way anyone finds out the site works as an app is being told. Only Safari
 * can add to the home screen on iOS (Chrome and the in-app browsers cannot,
 * or not reliably), so only Safari is told. It appears a few seconds into the
 * first visit, once - marked as shown the moment it shows, so it never comes
 * back whether or not it was dismissed.
 */
export const InstallHint = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOS() || isStandalone()) return undefined;
    const ua = navigator.userAgent;
    if (!/Safari\//.test(ua) || /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|FBAN|FBAV|Instagram|Line\//.test(ua)) return undefined;
    try {
      if (localStorage.getItem(HINT_KEY)) return undefined;
    } catch {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setShow(true);
      try {
        localStorage.setItem(HINT_KEY, String(Date.now()));
      } catch {
        // Private browsing: it may show again next time, which is harmless
      }
    }, 4000);
    return () => window.clearTimeout(timer);
  }, []);

  if (!show) return null;
  return (
    <div className="install-hint" role="status">
      <p>
        Watch like an app: tap{' '}
        <svg className="install-share" viewBox="0 0 24 24" fill="currentColor" aria-label="Share">
          <path d="M12 2.6 7.3 7.3l1.4 1.4L11 6.4V15h2V6.4l2.3 2.3 1.4-1.4L12 2.6zM5 10v10.5h14V10h-3v2h1v6.5H7V12h1v-2H5z" />
        </svg>{' '}
        then <b>Add to Home Screen</b>.
      </p>
      <button type="button" className="install-close" aria-label="Dismiss" onClick={() => setShow(false)}>
        ×
      </button>
    </div>
  );
};
